const CHEVRON = `<svg xmlns="http://www.w3.org/2000/svg" class="header-icon-chevron contact-form-chevron" width="10" height="6" viewBox="0 0 9.63882 5.56542" fill="none" aria-hidden="true" focusable="false"><path d="M0.731445 0.5C0.792174 0.500058 0.834742 0.51595 0.883789 0.564453V0.56543L4.4707 4.17188L4.82422 4.52734L8.78516 0.566406C8.83459 0.516969 8.86843 0.508204 8.91113 0.509766C8.96727 0.511885 9.01565 0.529318 9.07324 0.586914C9.12284 0.636599 9.13867 0.679771 9.13867 0.741211C9.13864 0.80263 9.12292 0.845796 9.07324 0.895508L4.97363 4.99414C4.9541 5.01367 4.93772 5.02716 4.9248 5.03613L4.89648 5.05176C4.87745 5.05969 4.85518 5.06539 4.8252 5.06543C4.79504 5.06543 4.77203 5.05972 4.75293 5.05176C4.74124 5.04686 4.71452 5.03288 4.67578 4.99414L0.555664 0.874023C0.51061 0.828834 0.498131 0.793316 0.5 0.738281C0.502397 0.670355 0.523272 0.620312 0.577148 0.566406C0.62694 0.516614 0.669902 0.5 0.731445 0.5Z" fill="currentColor" stroke="currentColor"/></svg>`

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

// Zendesk system fields map to the request body; every other field is a custom
// field, addressed by `custom_field_<id>` so ids stay stable across renders.
function fieldName(field) {
  if (field.type === 'subject') return 'subject'
  if (field.type === 'description') return 'description'
  return `custom_field_${field.id}`
}

class ContactForm extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint || '/apps/api'
    this.sectionId = this.id.replace('ContactFormEl-', '')
    this.forms = []

    this.form = this.querySelector('[data-form]')
    this.issueTypeSelect = this.querySelector('[data-issue-type]')
    this.dynamicFields = this.querySelector('[data-dynamic-fields]')
    this.submitBtn = this.querySelector('[data-submit]')
    this.statusEl = this.querySelector('[data-status]')
    this.fileInput = this.querySelector('[data-file-input]')
    this.fileText = this.querySelector('[data-file-text]')

    this.bindFileInput()
    this.form.addEventListener('submit', (e) => this.handleSubmit(e))
    this.issueTypeSelect.addEventListener('change', () => this.renderFields())

    this.loadConfig()
  }

  async loadConfig() {
    this.setLoading(true)
    try {
      const response = await fetch(`${this.endpoint}/contact-form`, {
        headers: { Accept: 'application/json' },
      })

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`)
      }

      const data = await response.json()
      this.forms = Array.isArray(data.forms) ? data.forms : []

      if (!this.forms.length) {
        throw new Error('No contact forms configured')
      }

      this.renderIssueTypes()
      this.renderFields()
    } catch (err) {
      console.error('Failed to load contact form config:', err)
      this.showStatus(
        'Sorry, the contact form is unavailable right now. Please try again later.',
        'error',
      )
      this.submitBtn.disabled = true
    } finally {
      this.setLoading(false)
    }
  }

  get selectedForm() {
    return (
      this.forms.find((form) => String(form.id) === this.issueTypeSelect.value) ||
      this.forms[0]
    )
  }

  renderIssueTypes() {
    this.issueTypeSelect.innerHTML = this.forms
      .map((form) => `<option value="${form.id}">${escapeHtml(form.name)}</option>`)
      .join('')
  }

  renderFields() {
    const form = this.selectedForm
    this.dynamicFields.innerHTML = form
      ? form.fields.map((field) => this.fieldMarkup(field)).join('')
      : ''
  }

  fieldMarkup(field) {
    const id = `ContactField-${this.sectionId}-${field.id}`
    const name = fieldName(field)
    const required = field.required ? ' required' : ''
    const star = field.required ? ' <span aria-hidden="true">*</span>' : ''
    const description = field.description
      ? `<p class="contact-form-field-description">${escapeHtml(field.description)}</p>`
      : ''

    // A checkbox is a single boolean; its title lives beside the box, not above.
    if (field.type === 'checkbox') {
      return `<div class="contact-form-field">
        <label class="contact-form-checkbox" for="${id}">
          <input type="checkbox" id="${id}" name="${name}" value="1"${required}>
          <span>${escapeHtml(field.title)}${star}</span>
        </label>
        ${description}
      </div>`
    }

    const label = `<label class="contact-form-label" for="${id}">${escapeHtml(field.title)}${star}</label>`
    return `<div class="contact-form-field">${label}${description}${this.controlMarkup(field, id, name, required)}</div>`
  }

  controlMarkup(field, id, name, required) {
    switch (field.type) {
      case 'description':
      case 'textarea':
        return `<textarea class="contact-form-textarea" id="${id}" name="${name}" rows="6"${required}></textarea>`

      case 'tagger': {
        const options = ['<option value="">Please select…</option>']
          .concat(
            (field.options || []).map(
              (option) =>
                `<option value="${escapeHtml(option.value)}">${escapeHtml(option.name)}</option>`,
            ),
          )
          .join('')
        return `<div class="contact-form-select-wrap"><select class="contact-form-select" id="${id}" name="${name}"${required}>${options}</select>${CHEVRON}</div>`
      }

      case 'multiselect': {
        const boxes = (field.options || [])
          .map((option, index) => {
            const optionId = `${id}-${index}`
            const box = `<input type="checkbox" id="${optionId}" name="${name}" value="${escapeHtml(option.value)}">`
            return `<label class="contact-form-checkbox" for="${optionId}">${box}<span>${escapeHtml(option.name)}</span></label>`
          })
          .join('')
        return `<div class="contact-form-checkboxes" role="group">${boxes}</div>`
      }

      case 'integer':
        return `<input class="contact-form-input" type="number" step="1" id="${id}" name="${name}"${required}>`

      case 'decimal':
        return `<input class="contact-form-input" type="number" step="any" id="${id}" name="${name}"${required}>`

      case 'date':
        return `<input class="contact-form-input" type="date" id="${id}" name="${name}"${required}>`

      case 'regexp': {
        const pattern = field.regexp ? ` pattern="${escapeHtml(field.regexp)}"` : ''
        return `<input class="contact-form-input" type="text" id="${id}" name="${name}"${required}${pattern}>`
      }

      case 'subject':
      case 'text':
      default:
        return `<input class="contact-form-input" type="text" id="${id}" name="${name}"${required}>`
    }
  }

  bindFileInput() {
    if (!this.fileInput) return
    this.fileInput.addEventListener('change', () => {
      const files = Array.from(this.fileInput.files)
      this.fileText.textContent = files.length
        ? files.map((file) => file.name).join(', ')
        : 'Choose a file or drag and drop here'
    })
  }

  readField(field) {
    const name = fieldName(field)

    if (field.type === 'multiselect') {
      return Array.from(this.form.querySelectorAll(`[name="${name}"]:checked`)).map(
        (el) => el.value,
      )
    }

    if (field.type === 'checkbox') {
      const el = this.form.querySelector(`[name="${name}"]`)
      return el ? el.checked : false
    }

    const el = this.form.querySelector(`[name="${name}"]`)
    if (!el) return ''
    const value = el.value.trim()

    if ((field.type === 'integer' || field.type === 'decimal') && value !== '') {
      return Number(value)
    }

    return value
  }

  async handleSubmit(e) {
    e.preventDefault()

    if (!this.form.checkValidity()) {
      this.form.reportValidity()
      return
    }

    const form = this.selectedForm
    if (!form) return

    this.setLoading(true)
    this.showStatus('')

    let subject = ''
    let description = ''
    const customFields = []

    for (const field of form.fields) {
      const value = this.readField(field)
      if (field.type === 'subject') {
        subject = value
      } else if (field.type === 'description') {
        description = value
      } else {
        customFields.push({ id: field.id, value })
      }
    }

    const payload = {
      ticket_form_id: form.id,
      email: this.form.querySelector('[name="email"]').value.trim(),
      subject,
      description,
      custom_fields: customFields,
    }

    try {
      const response = await fetch(`${this.endpoint}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const json = await response.json()

      if (!response.ok) {
        throw new Error(json.error || `Request failed (${response.status})`)
      }

      this.showStatus("Your message has been sent. We'll be in touch soon.", 'success')
      this.form.reset()
      this.renderFields()
      if (this.fileText) {
        this.fileText.textContent = 'Choose a file or drag and drop here'
      }
    } catch (err) {
      this.showStatus(err.message || 'Something went wrong. Please try again.', 'error')
    } finally {
      this.setLoading(false)
    }
  }

  setLoading(loading) {
    this.submitBtn.disabled = loading
    this.submitBtn.textContent = loading ? 'Sending…' : 'Submit'
  }

  showStatus(message, type = '') {
    if (!message) {
      this.statusEl.hidden = true
      this.statusEl.textContent = ''
      this.statusEl.className = 'contact-form-status'
      return
    }

    this.statusEl.textContent = message
    this.statusEl.className = `contact-form-status contact-form-status-${type}`
    this.statusEl.hidden = false
    this.statusEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }
}

customElements.define('contact-form', ContactForm)
